// ✅ FILE 3: src/pages/admin/PostEdit.jsx
// No logic change needed for TinyMCE; it already submits `content` as HTML.
// Kept as full file (only tiny cleanups).

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import PostEditor from "./PostEditor";
import { getAdminPost, updateAdminPost } from "../../lib/adminApi";

const EMPTY_FORM = {
  title: "",
  excerpt: "",
  content: "<p></p>",

  // ✅ multi categories (frontend)
  categories: ["humanitarian_impact"],

  // ✅ primary for backend compatibility
  category: "humanitarian_impact",

  author: "",
  image_url: "",
  tags: [],
  is_published: true,
};

const PostEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadPost = async () => {
    setLoading(true);
    try {
      const data = await getAdminPost(id);

      const cats = Array.isArray(data?.categories)
        ? data.categories.map((c) => String(c || "").trim()).filter(Boolean)
        : [];

      const primary =
        (data?.category && String(data.category).trim()) ||
        cats[0] ||
        "humanitarian_impact";

      setForm({
        title: data?.title || "",
        excerpt: data?.excerpt || "",
        content: (data?.content && String(data.content)) || "<p></p>",

        categories: cats.length ? cats : [primary],
        category: primary,

        author: data?.author || "",
        image_url: data?.image_url || "",
        tags: Array.isArray(data?.tags) ? data.tags : [],
        is_published:
          typeof data?.is_published === "boolean" ? data.is_published : true,
      });
    } catch (err) {
      alert(err?.message || "Failed to load post");
      navigate("/admin/posts", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) {
      navigate("/admin/posts", { replace: true });
      return;
    }
    loadPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const cats = Array.isArray(form.categories)
        ? form.categories.map((c) => String(c || "").trim()).filter(Boolean)
        : [];

      const primary =
        (form.category && String(form.category).trim()) ||
        cats[0] ||
        "humanitarian_impact";

      const finalCats = cats.length ? cats : [primary];

      const fd = new FormData();
      fd.append("title", (form.title || "").trim());
      fd.append("excerpt", (form.excerpt || "").trim());
      fd.append("content", (form.content || "<p></p>").trim());

      // ✅ backend-safe: always send the primary category
      fd.append("category", primary);

      // ✅ only matters if your backend supports it
      fd.append("categories_json", JSON.stringify(finalCats));

      fd.append("author", (form.author || "").trim());
      fd.append("image_url", (form.image_url || "").trim());
      fd.append("is_published", String(!!form.is_published));
      fd.append("tags_json", JSON.stringify(Array.isArray(form.tags) ? form.tags : []));

      await updateAdminPost(id, fd);
      navigate("/admin/posts", { replace: true });
    } catch (err) {
      alert(err?.message || "Failed to update post");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Edit Blog Post">
        <div className="rounded-2xl border border-stone-200 bg-white p-6">
          Loading post...
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Edit Blog Post">
      <PostEditor
        form={form}
        setForm={setForm}
        onSubmit={submit}
        loading={saving}
        submitText="Update Post"
      />
    </AdminLayout>
  );
};

export default PostEdit;